
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/providers/AuthProvider";
import { CartProvider } from "@/contexts/CartContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import * as Sentry from "@sentry/react";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { usePWADetection } from "@/hooks/usePWADetection";

// Landing pages
import LandingPage from "./pages/LandingPage";
import InstallGuidePage from "./pages/InstallGuidePage";
import AboutUsPage from "./pages/AboutUsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsConditionsPage from "./pages/TermsConditionsPage";

// App pages
import SplashScreen from "./pages/SplashScreen";
import AuthPage from "./pages/AuthPage";
import HomePage from "./pages/HomePage";
import RestaurantPage from "./pages/RestaurantPage";
import MarketplaceVendorPage from "./pages/MarketplaceVendorPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import ProfilePage from "./pages/ProfilePage";
import SettingsPage from "./pages/SettingsPage";
import EarningsPage from "./pages/EarningsPage";
import GetVerifiedPage from "./pages/GetVerifiedPage";
import EditProfilePage from "./pages/EditProfilePage";
import OrderHistoryPage from "./pages/OrderHistoryPage";
import MyOrdersPage from "./pages/MyOrdersPage";
import FavoritesPage from "./pages/FavoritesPage";
import LocationPage from "./pages/LocationPage";
import PromoPage from "./pages/PromoPage";
import ExplorePage from "./pages/ExplorePage";
import GroceryPage from "./pages/GroceryPage";
import ShopAbroadSheinPage from "./pages/ShopAbroadSheinPage";
import { MarketplaceCategoryPage } from "./pages/MarketplaceCategoryPage";
import MarketplaceDealsPage from "./pages/MarketplaceDealsPage";
import MarketplaceCartPage from "./pages/MarketplaceCartPage";
import VendorOnboardingPage from "./pages/VendorOnboardingPage";
import DriverApplicationPage from "./pages/DriverApplicationPage";
import BusinessDashboardPage from "./pages/BusinessDashboardPage";
import DriverDashboardPage from "./pages/DriverDashboardPage";
import SwapRequestsPage from "./pages/SwapRequestsPage";
import ReferralPage from "./pages/ReferralPage";
import RideBookingPage from "./pages/RideBookingPage";
import RideSchedulePage from "./pages/RideSchedulePage";
import RideRentalsPage from "./pages/RideRentalsPage";
import RideRentalCarsPage from "./pages/RideRentalCarsPage";
import RideDriverDashboardPage from "./pages/RideDriverDashboardPage";
import RentalDriverDashboardPage from "./pages/RentalDriverDashboardPage";
import RideTrackingPage from "./pages/RideTrackingPage";
import RideActivitiesPage from "./pages/RideActivitiesPage";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import NotFound from "./pages/NotFound";
import AutoSubscribeNotifications from "./components/AutoSubscribeNotifications";
import PendingRatingPrompt from "./components/PendingRatingPrompt";

const queryClient = new QueryClient();

const AppRoutes = () => {
  const { isInstalled, loading } = usePWADetection();

  // Check for median.co wrapper or specific URL patterns for app store deployment
  const isMedianWrapper = window.location.hostname.includes('median.co') || 
                          window.navigator.userAgent.includes('median') ||
                          (window as any).median !== undefined;
  
  const isAppStoreDeployment = isMedianWrapper ||
                               window.location.pathname.startsWith('/auth') ||
                               window.location.search.includes('app=true');

  // For median.co, bypass loading check and always show app routes
  if (isMedianWrapper || (isInstalled && !loading) || (!loading && isAppStoreDeployment)) {
    return (
      <Routes>
        <Route path="/" element={isAppStoreDeployment ? <AuthPage /> : <SplashScreen />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/restaurant/:id" element={<RestaurantPage />} />
        <Route path="/marketplace/vendor/:id" element={<MarketplaceVendorPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/:userId" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/earnings" element={<EarningsPage />} />
              <Route path="/get-verified" element={<GetVerifiedPage />} />
        <Route path="/edit-profile" element={<EditProfilePage />} />
        <Route path="/order-history" element={<OrderHistoryPage />} />
        <Route path="/orders" element={<MyOrdersPage />} />
        <Route path="/favorites" element={<FavoritesPage />} />
        <Route path="/location" element={<LocationPage />} />
        <Route path="/promo" element={<PromoPage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/grocery" element={<GroceryPage />} />
        <Route path="/shop-abroad/shein" element={<ShopAbroadSheinPage />} />
        <Route path="/marketplace/:categoryId" element={<MarketplaceCategoryPage />} />
        <Route path="/marketplace/deals" element={<MarketplaceDealsPage />} />
        <Route path="/marketplace/cart" element={<MarketplaceCartPage />} />
        <Route path="/vendor-onboarding" element={<VendorOnboardingPage />} />
        <Route path="/driver-application" element={<DriverApplicationPage />} />
        <Route path="/business-dashboard" element={<BusinessDashboardPage />} />
        <Route path="/driver-dashboard" element={<DriverDashboardPage />} />
        <Route path="/ride-driver-dashboard" element={<RideDriverDashboardPage />} />
        <Route path="/rental-driver-dashboard" element={<RentalDriverDashboardPage />} />
        <Route path="/swap-requests" element={<SwapRequestsPage />} />
        <Route path="/referral" element={<ReferralPage />} />
        <Route path="/ride" element={<RideBookingPage />} />
        <Route path="/ride/schedule" element={<RideSchedulePage />} />
        <Route path="/ride/rentals" element={<RideRentalsPage />} />
        <Route path="/ride/rentals/cars" element={<RideRentalCarsPage />} />
        <Route path="/ride/track/:rideId" element={<RideTrackingPage />} />
        <Route path="/ride/activities" element={<RideActivitiesPage />} />
        <Route path="/track-order/:orderId" element={<OrderTrackingPage />} />
        <Route path="*" element={<Navigate to="/auth" replace />} />
      </Routes>
    );
  }

  // Show loading spinner for non-median environments while detecting PWA
  if (loading && !isMedianWrapper) {
    return <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
    </div>;
  }

  // Show landing page flow for non-installed users
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/install-guide" element={<InstallGuidePage />} />
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsConditionsPage />} />
      <Route path="/ride/track/:rideId" element={<RideTrackingPage />} />
      {/* Shared vendor links - allow non-installed users to browse and order */}
      <Route path="/restaurant/:id" element={<RestaurantPage />} />
      <Route path="/marketplace/vendor/:id" element={<MarketplaceVendorPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/marketplace/cart" element={<MarketplaceCartPage />} />
      <Route path="/order-history" element={<OrderHistoryPage />} />
      <Route path="/orders" element={<MyOrdersPage />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/profile/:userId" element={<ProfilePage />} />
      <Route path="/location" element={<LocationPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/grocery" element={<GroceryPage />} />
      <Route path="/shop-abroad/shein" element={<ShopAbroadSheinPage />} />
      <Route path="/marketplace/:categoryId" element={<MarketplaceCategoryPage />} />
      <Route path="/marketplace/deals" element={<MarketplaceDealsPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => (
  <Sentry.ErrorBoundary 
    fallback={({ error, resetError }) => (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-destructive mb-4">Something went wrong</h2>
          <p className="text-muted-foreground mb-4">
            We're sorry, but something unexpected happened. Our team has been notified.
          </p>
          <button 
            onClick={resetError}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    )}
    beforeCapture={(scope) => {
      scope.setTag('errorBoundary', true);
    }}
  >
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <LanguageProvider>
          <BrowserRouter>
            <AuthProvider>
              <CartProvider>
                <AutoSubscribeNotifications />
                <PendingRatingPrompt />
                
                <AppRoutes />
              </CartProvider>
            </AuthProvider>
          </BrowserRouter>
        </LanguageProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
